import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { Box, Typography, Button, Container } from "@mui/material";
import { WarningAmberRounded } from "@mui/icons-material";
import { motion } from "framer-motion";

/**
 * FerrariErrorBoundary
 * Post-Mediocrity Error Handling: "System Malfunction" instead of generic crashes.
 */
class FerrariErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Critical System Failure:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    const { t } = this.props;
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            height: "100vh",
            width: "100vw",
            bgcolor: "var(--mui-palette-background-default)",
            color: "var(--text-primary)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Background Noise/Grid */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.1,
              backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "30px 30px",
            }}
          />

          <Container maxWidth="sm" sx={{ position: "relative", zIndex: 10, textAlign: "center" }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 100 }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  p: 3,
                  borderRadius: "50%",
                  bgcolor: "rgba(239, 68, 68, 0.1)",
                  border: "1px solid rgba(239, 68, 68, 0.2)",
                  mb: 4,
                  boxShadow: "0 0 40px rgba(239, 68, 68, 0.2)",
                }}
              >
                <WarningAmberRounded sx={{ fontSize: 60, color: "#ef4444" }} />
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontFamily: '"Oswald", sans-serif',
                  fontSize: "4rem",
                  fontWeight: 900,
                  letterSpacing: -2,
                  textTransform: "uppercase",
                  mb: 2,
                  background: "linear-gradient(to bottom, #fff 50%, color-mix(in srgb, var(--text-primary) 50%, transparent))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t('common.error_boundary.title')}
              </Typography>

              <Typography variant="body1" sx={{ color: "#94a3b8", mb: 6, fontFamily: '"Inter", sans-serif' }}>
                 {t('common.error_boundary.desc_1')} 
                 <br />
                 {t('common.error_boundary.desc_2')}
              </Typography>

              <Button
                variant="outlined"
                onClick={this.handleReset}
                sx={{
                  color: "#ef4444",
                  borderColor: "rgba(239, 68, 68, 0.5)",
                  px: 4,
                  py: 1.5,
                  borderRadius: "12px",
                  fontWeight: 700,
                  "&:hover": {
                    borderColor: "#ef4444",
                    bgcolor: "rgba(239, 68, 68, 0.1)",
                  },
                }}
              >
                {t('common.error_boundary.reboot')}
              </Button>
            </motion.div>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default withTranslation()(FerrariErrorBoundary);
